import { search } from 'jmespath'
import { useCallback } from 'react'
import { graphqlClient } from '../../api/graphqlClient'
import { USER } from '../../store/redux/constatnts'
import { dispatchType } from '../../store/storeInstance'
import { retryUntil } from '@sceneio/tools'
import ReactGA from 'react-ga4'

type ObjectType = Record<string, unknown>

function resolveSuccessLogin<
  T extends string,
  U extends ObjectType,
  S extends ObjectType,
  I extends boolean,
>(token: T, user: U, selectedProject: S, isFirstLogin: I) {
  if (!token) {
    throw new Error('Invalid Credentials')
  }

  const { name, id, email, createdAt, hasCompletedSegmentation } = user

  dispatchType(USER.LOGIN_SUCCESS, {
    id,
    name,
    email,
    token,
    selectedProject,
    createdAt,
    isFirstLogin,
    hasCompletedSegmentation,
  })

  return {
    data: { token, isFirstLogin },
    errors: [],
  }
}

function resolveFailedLogin(err: Error) {
  console.warn(err)
  dispatchType(USER.LOGIN_FAIL)

  return {
    data: null,
    error: err.message,
  }
}

type AuthAction = 'SIGNIN' | 'SIGNUP'

function trackIntercomEvent({
  authAction,
  isFirstLogin,
}: {
  authAction: AuthAction
  isFirstLogin?: boolean
}) {
  if (import.meta.env.PROD) {
    retryUntil(
      // checkFn
      () => 'Intercom' in window,
      // successFn
      () => {
        if (authAction === 'SIGNUP' || isFirstLogin) {
          ;(window as any).Intercom('trackEvent', 'user-created')
        }
      },
      // failFn
      () => {
        console.warn('Intercom can not be initialized')
      },
      12,
      250,
    )
  }
}

function trackGAEvent({
  userId,
  authAction,
  isFirstLogin,
}: {
  userId: string
  authAction: AuthAction
  isFirstLogin?: boolean
}) {
  if (authAction === 'SIGNUP' || isFirstLogin) {
    ReactGA.event('userSignUp', { userId })
    return
  }
  if (authAction === 'SIGNIN') {
    ReactGA.event('userSignIn', { userId })
  }
}

export function useGoogleLogin() {
  return useCallback(
    async (
      payload: { token: string; urlParams?: Record<string, any> } = {
        token: '',
      },
    ) => {
      try {
        const responseData = await graphqlClient.graphql(
          'LoginGoogleMutationDocument',
          payload,
        )

        const token = responseData?.data?.googleLogin?.token?.value!
        const selectedProject = responseData?.data?.googleLogin?.project!
        const user = responseData?.data?.googleLogin?.user!
        const authAction = responseData?.data?.googleLogin
          ?.authAction! as AuthAction
        const isFirstLogin = responseData?.data?.googleLogin?.isFirstLogin!

        trackIntercomEvent({ authAction, isFirstLogin })
        trackGAEvent({ userId: user.id as string, authAction, isFirstLogin })

        return resolveSuccessLogin(token, user, selectedProject, isFirstLogin)
      } catch (err: any) {
        return resolveFailedLogin(err)
      }
    },
    [],
  )
}

export function useTokenLogin() {
  return useCallback(
    async (
      payload: { token: string; urlParams?: Record<string, any> } = {
        token: '',
      },
    ) => {
      try {
        const responseData = await graphqlClient.graphql(
          'LoginTokenMutationDocument',
          payload,
        )

        const token = search(responseData, 'data.tokenLogin.token.value')
        const selectedProject = search(responseData, 'data.tokenLogin.project')
        const user = search(responseData, 'data.tokenLogin.user') || {}
        const authAction = search(
          responseData,
          'data.tokenLogin.authAction',
        ) as AuthAction
        const isFirstLogin = search(
          responseData,
          'data.tokenLogin.isFirstLogin',
        )

        trackIntercomEvent({ authAction, isFirstLogin })
        trackGAEvent({ userId: user.id as string, authAction, isFirstLogin })

        return resolveSuccessLogin(token, user, selectedProject, isFirstLogin)
      } catch (err: any) {
        return resolveFailedLogin(err)
      }
    },
    [],
  )
}

export function useEmailLogin() {
  return useCallback(
    async (
      payload: {
        email: string
        token: string
        urlParams?: Record<string, any>
      } = { email: '', token: '' },
    ) => {
      try {
        const responseData = await graphqlClient.graphql(
          'LoginEmailMutationDocument',
          payload,
        )

        const success = search(
          responseData,
          'data.emailLogin.success',
        ) as boolean
        const message = search(
          responseData,
          'data.emailLogin.message',
        ) as string

        return {
          success,
          message,
        }
      } catch (err: any) {
        return {
          success: false,
          message: 'Sending failed',
        }
      }
    },
    [],
  )
}
